import { useEffect, useMemo, useState } from "react"
import { AlertTriangle, Copy, ExternalLink, Loader2 } from "lucide-react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import * as z from "zod"
import { zodResolver } from "@hookform/resolvers/zod"

import {
  Alert,
  AlertDescription,
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from "@f2e/ui/components/base"
import { Agent, normalizeLineSkipKeywords } from "@/types"
import { useSysConfig, useToast } from "@/hooks"
import { agentResponseToAgent, useGetPublishedAgentsApi } from "@/hooks/api"
import { isMaskedValue } from "../utils"

const DEFAULT_CONVERSATION_SESSION_TIMEOUT_HOURS = 24

export interface LineConnectionData {
  channelSecret: string
  channelAccessToken: string
  liffId: string
  lineLoginChannelId: string
  agentId: string
  requireEmailVerification: boolean
  skipKeywords: string[]
  conversationSessionTimeoutHours: number
}

export interface LineConnectionFormProps {
  channelId: string
  workspaceId: string
  organizationId: string
  initialData?: Partial<LineConnectionData>
  onSubmit: (data: LineConnectionData) => void
  onCancel?: () => void
  isLoading?: boolean
  onDirtyChange?: (isDirty: boolean) => void
}

const createLineFormSchema = (t: (key: string) => string) =>
  z
    .object({
      channelSecret: z.string().min(1, {
        message: t("workspace.channels.line.form.errors.channelSecretRequired"),
      }),
      channelAccessToken: z.string().min(1, {
        message: t(
          "workspace.channels.line.form.errors.channelAccessTokenRequired"
        ),
      }),
      liffId: z.string(),
      lineLoginChannelId: z.string(),
      agentId: z.string().min(1, {
        message: t("workspace.channels.line.form.errors.agentRequired"),
      }),
      requireEmailVerification: z.boolean(),
      skipKeywordsText: z.string(),
      conversationSessionTimeoutHours: z.coerce
        .number()
        .int({
          message: t(
            "workspace.channels.line.form.errors.conversationSessionTimeoutHoursInteger"
          ),
        })
        .min(1, {
          message: t(
            "workspace.channels.line.form.errors.conversationSessionTimeoutHoursRange"
          ),
        })
        .max(720, {
          message: t(
            "workspace.channels.line.form.errors.conversationSessionTimeoutHoursRange"
          ),
        }),
    })
    .superRefine((data, ctx) => {
      if (!data.requireEmailVerification) {
        return
      }

      if (!data.liffId.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["liffId"],
          message: t("workspace.channels.line.form.errors.liffIdRequired"),
        })
      }

      if (!data.lineLoginChannelId.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lineLoginChannelId"],
          message: t(
            "workspace.channels.line.form.errors.lineLoginChannelIdRequired"
          ),
        })
      }
    })

type LineFormData = z.infer<ReturnType<typeof createLineFormSchema>>

const getLineFormDefaults = (
  initialData?: Partial<LineConnectionData>
): LineFormData => ({
  channelSecret: initialData?.channelSecret || "",
  channelAccessToken: initialData?.channelAccessToken || "",
  liffId: initialData?.liffId || "",
  lineLoginChannelId: initialData?.lineLoginChannelId || "",
  agentId: initialData?.agentId || "",
  requireEmailVerification: initialData?.requireEmailVerification ?? true,
  skipKeywordsText: (initialData?.skipKeywords ?? []).join("\n"),
  conversationSessionTimeoutHours:
    initialData?.conversationSessionTimeoutHours ??
    DEFAULT_CONVERSATION_SESSION_TIMEOUT_HOURS,
})

const getKeywordsWithBoundaryWhitespace = (keywordsText: string): string[] =>
  normalizeLineSkipKeywords(keywordsText.split("\n")).filter(
    (keyword) => keyword.trim() !== keyword
  )

const buildApiUrl = (serverRoot: string, path: string) => {
  if (!serverRoot) {
    return path
  }
  return new URL(path, serverRoot).toString()
}

export const LineConnectionForm = ({
  channelId,
  workspaceId,
  organizationId,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  onDirtyChange,
}: LineConnectionFormProps) => {
  const { t } = useTranslation()
  const { successToast } = useToast()
  const sysConfig = useSysConfig()
  const [agents, setAgents] = useState<Agent[]>([])

  const initialChannelSecret = initialData?.channelSecret
  const initialChannelAccessToken = initialData?.channelAccessToken

  const skipKeywordsText = useMemo(
    () => (initialData?.skipKeywords ?? []).join("\n"),
    [initialData?.skipKeywords]
  )

  const formDefaults = useMemo(
    () =>
      getLineFormDefaults({
        channelSecret: initialData?.channelSecret,
        channelAccessToken: initialData?.channelAccessToken,
        liffId: initialData?.liffId,
        lineLoginChannelId: initialData?.lineLoginChannelId,
        agentId: initialData?.agentId,
        requireEmailVerification: initialData?.requireEmailVerification,
        skipKeywords: skipKeywordsText ? skipKeywordsText.split("\n") : [],
        conversationSessionTimeoutHours:
          initialData?.conversationSessionTimeoutHours,
      }),
    [
      initialData?.agentId,
      initialData?.channelAccessToken,
      initialData?.channelSecret,
      initialData?.liffId,
      initialData?.lineLoginChannelId,
      initialData?.requireEmailVerification,
      skipKeywordsText,
      initialData?.conversationSessionTimeoutHours,
    ]
  )

  const LineFormSchema = createLineFormSchema(t)
  const form = useForm<LineFormData>({
    resolver: zodResolver(LineFormSchema),
    defaultValues: formDefaults,
  })
  const { isDirty } = form.formState
  const requireEmailVerification = form.watch("requireEmailVerification")
  const skipKeywordsInput = form.watch("skipKeywordsText")
  const keywordsWithBoundaryWhitespace = useMemo(
    () => getKeywordsWithBoundaryWhitespace(skipKeywordsInput),
    [skipKeywordsInput]
  )

  const webhookUrl = useMemo(
    () =>
      channelId
        ? buildApiUrl(sysConfig.serverRoot, `/api/v1/line/events/${channelId}`)
        : "",
    [channelId, sysConfig.serverRoot]
  )
  const liffEntryUrl = useMemo(
    () =>
      channelId
        ? buildApiUrl(
            sysConfig.serverRoot,
            `/api/v1/line/bind-email?channel_id=${channelId}`
          )
        : "",
    [channelId, sysConfig.serverRoot]
  )

  const { data: agentsRespData, fetchFn: getAgents } =
    useGetPublishedAgentsApi()

  useEffect(() => {
    if (!workspaceId || !organizationId) return
    getAgents({
      searchParams: new URLSearchParams({
        wid: workspaceId,
        oid: organizationId,
      }),
    })
  }, [getAgents, workspaceId, organizationId])

  useEffect(() => {
    if (agentsRespData?.data) {
      setAgents(agentsRespData.data.map(agentResponseToAgent))
    }
  }, [agentsRespData])

  useEffect(() => {
    form.reset(formDefaults)
  }, [channelId, form, formDefaults])

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    if (!requireEmailVerification) {
      form.clearErrors(["liffId", "lineLoginChannelId"])
    }
  }, [form, requireEmailVerification])

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    successToast({
      title: t("common.copied"),
      description: t("common.copiedToClipboard"),
    })
  }

  const handleSubmit = (data: LineFormData) => {
    onSubmit({
      channelSecret: data.channelSecret,
      channelAccessToken: data.channelAccessToken,
      liffId: data.liffId,
      lineLoginChannelId: data.lineLoginChannelId,
      agentId: data.agentId,
      requireEmailVerification: data.requireEmailVerification,
      skipKeywords: normalizeLineSkipKeywords(
        data.skipKeywordsText.split("\n")
      ),
      conversationSessionTimeoutHours: data.conversationSessionTimeoutHours,
    })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <Alert>
          <AlertDescription>
            <div className="space-y-3">
              <p className="font-medium">
                {t("workspace.channels.line.setup.title")}
              </p>
              <pre className="whitespace-pre-wrap font-sans text-sm text-muted-foreground">
                {t("workspace.channels.line.setup.instructions")}
              </pre>
              <a
                href="https://developers.line.biz/console/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                {t("workspace.channels.line.setup.openConsole")}
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </AlertDescription>
        </Alert>

        {webhookUrl && (
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t("workspace.channels.line.form.webhookUrl")}
            </label>
            <div className="flex items-center gap-2">
              <Input
                value={webhookUrl}
                readOnly
                className="font-mono text-xs bg-muted"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(webhookUrl)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("workspace.channels.line.form.webhookUrlDescription")}
            </p>
          </div>
        )}

        {liffEntryUrl && (
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t("workspace.channels.line.form.bindEmailUrl")}
            </label>
            <div className="flex items-center gap-2">
              <Input
                value={liffEntryUrl}
                readOnly
                className="font-mono text-xs bg-muted"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(liffEntryUrl)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("workspace.channels.line.form.bindEmailUrlDescription")}
            </p>
          </div>
        )}

        <FormField
          control={form.control}
          name="channelSecret"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("workspace.channels.line.form.channelSecret")}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder={t(
                    "workspace.channels.line.form.channelSecretPlaceholder"
                  )}
                  className="font-mono"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                {t("workspace.channels.line.form.channelSecretDescription")}
                {isMaskedValue(initialChannelSecret) && (
                  <span className="block">
                    {t("workspace.channels.line.form.maskedValueNotice")}
                  </span>
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="channelAccessToken"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("workspace.channels.line.form.channelAccessToken")}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder={t(
                    "workspace.channels.line.form.channelAccessTokenPlaceholder"
                  )}
                  className="font-mono"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                {t(
                  "workspace.channels.line.form.channelAccessTokenDescription"
                )}
                {isMaskedValue(initialChannelAccessToken) && (
                  <span className="block">
                    {t("workspace.channels.line.form.maskedValueNotice")}
                  </span>
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="requireEmailVerification"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("workspace.channels.line.form.requireEmailVerification")}
              </FormLabel>
              <div className="flex items-center gap-3">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormDescription className="m-0">
                  {t(
                    "workspace.channels.line.form.requireEmailVerificationDescription"
                  )}
                </FormDescription>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="liffId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("workspace.channels.line.form.liffId")}{" "}
                {requireEmailVerification && (
                  <span className="text-destructive">*</span>
                )}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t(
                    "workspace.channels.line.form.liffIdPlaceholder"
                  )}
                  className="font-mono"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                {t("workspace.channels.line.form.liffIdDescription")}
                {!requireEmailVerification && (
                  <span className="block">
                    {t("workspace.channels.line.form.liffOptionalHint")}
                  </span>
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lineLoginChannelId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("workspace.channels.line.form.lineLoginChannelId")}{" "}
                {requireEmailVerification && (
                  <span className="text-destructive">*</span>
                )}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t(
                    "workspace.channels.line.form.lineLoginChannelIdPlaceholder"
                  )}
                  className="font-mono"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                {t(
                  "workspace.channels.line.form.lineLoginChannelIdDescription"
                )}
                {!requireEmailVerification && (
                  <span className="block">
                    {t("workspace.channels.line.form.liffOptionalHint")}
                  </span>
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="agentId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("workspace.channels.line.form.agent")}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "workspace.channels.line.form.agentPlaceholder"
                      )}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                {t("workspace.channels.line.form.agentDescription")}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="skipKeywordsText"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("workspace.channels.line.form.skipKeywords")}
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t(
                    "workspace.channels.line.form.skipKeywordsPlaceholder"
                  )}
                  className="min-h-24 font-mono"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                {t("workspace.channels.line.form.skipKeywordsDescription")}
              </FormDescription>
              {keywordsWithBoundaryWhitespace.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                  <div className="flex gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-amber-600 dark:text-amber-300" />
                    <div className="space-y-2">
                      <p className="font-medium">
                        {t(
                          "workspace.channels.line.form.skipKeywordsWhitespaceWarningTitle"
                        )}
                      </p>
                      <p>
                        {t(
                          "workspace.channels.line.form.skipKeywordsWhitespaceWarningDescription"
                        )}
                      </p>
                      <div>
                        <p className="text-xs font-medium text-amber-900 dark:text-amber-200">
                          {t(
                            "workspace.channels.line.form.skipKeywordsWhitespaceWarningAffected"
                          )}
                        </p>
                        <ul className="mt-1 list-disc space-y-1 pl-4">
                          {keywordsWithBoundaryWhitespace.map((keyword) => (
                            <li key={keyword}>
                              <code className="rounded bg-amber-100 px-1 py-0.5 font-mono text-xs whitespace-pre-wrap text-amber-950 dark:bg-amber-900/70 dark:text-amber-50">
                                「{keyword}」
                              </code>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="conversationSessionTimeoutHours"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(
                  "workspace.channels.line.form.conversationSessionTimeoutHours"
                )}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input type="number" min={1} max={720} step={1} {...field} />
              </FormControl>
              <FormDescription>
                {t(
                  "workspace.channels.line.form.conversationSessionTimeoutHoursDescription"
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              {t("common.cancel")}
            </Button>
          )}
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("common.saving")}
              </>
            ) : (
              t("common.save")
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}

export default LineConnectionForm
